/**
 * `@horizon/i18n` — the single source of every user-facing string in Horizon.
 *
 * Arabic is the default and the primary design direction; French is equal but
 * secondary (`docs/04-design-system.md` §1). Both catalogues carry the same key tree;
 * `scripts/check-i18n.mjs` fails the build when they drift (§7.2 `i18n-keys-complete`,
 * US-X02 criterion 2).
 */

import i18next, { type i18n } from 'i18next'

import ar from './locales/ar.json' with { type: 'json' }
import fr from './locales/fr.json' with { type: 'json' }
import { DEFAULT_LOCALE, type Locale } from './locale.ts'

export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_META,
  isRTL,
  directionOf,
  isSupportedLocale,
  resolveLocale,
  type Locale,
  type Direction,
  type LocaleMeta,
} from './locale.ts'

export {
  NUMERAL_SYSTEM,
  THIN_SPACE,
  RELATIVE_TIME_CUTOFF_MS,
  toNumerals,
  formatDuration,
  formatWatchTime,
  formatPrice,
  formatInteger,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatFileSize,
  formatPercent,
  type NumeralSystem,
} from './format.ts'

export {
  LRI,
  RLI,
  FSI,
  PDI,
  MIRRORED_ICONS,
  ltrIsolate,
  firstStrongIsolate,
  isIsolated,
  stripIsolates,
  shouldMirrorIcon,
} from './bidi.ts'

/** The only namespace. Feature separation is by top-level key, not by i18next namespace. */
export const DEFAULT_NAMESPACE = 'translation'

/** The i18next `resources` object, built from the two catalogues. */
export const resources = {
  ar: { [DEFAULT_NAMESPACE]: ar },
  fr: { [DEFAULT_NAMESPACE]: fr },
} as const

/**
 * Shape of the Arabic catalogue — the reference tree. French is checked against it by
 * `scripts/check-i18n.mjs`, so a key that type-checks here exists in both files.
 */
export type TranslationCatalogue = typeof ar

/**
 * Create an initialised i18next instance for `locale`.
 *
 * A fresh instance rather than the global singleton, so tests, the admin panel's
 * bilingual preview, and server-rendered strings can each hold their own locale
 * without fighting over module state.
 *
 * There is no fallback to another language: a missing key must surface as the key
 * itself, not as silent French inside an Arabic screen.
 */
export function createI18n(locale: Locale = DEFAULT_LOCALE): i18n {
  const instance = i18next.createInstance()

  void instance.init({
    lng: locale,
    // Arabic is the reference catalogue; missing keys render as the key, never as
    // the other language.
    fallbackLng: false,
    supportedLngs: ['ar', 'fr'],
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE],
    resources,
    returnNull: false,
    // Values are interpolated into React Native <Text>, which does not evaluate HTML.
    interpolation: { escapeValue: false },
  })

  return instance
}
